import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, waLink, SEASONAL } from "@/lib/content";

const seasonalActive = (() => {
  if (!SEASONAL.enabled) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(SEASONAL.start) && today < SEASONAL.start) return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(SEASONAL.end) && today > SEASONAL.end) return false;
  return true;
})();

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.6 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// Works from any page: navigates home first when the target section is not mounted.
export const useGoTo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    const el = document.getElementById(id);
    if (el || pathname === "/") {
      if (id === "top" && pathname !== "/") {
        navigate("/");
        return;
      }
      scrollToId(id);
      if (!el) navigate("/");
      return;
    }
    navigate("/");
    setTimeout(() => scrollToId(id), 700);
  };
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goTo = useGoTo();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const go = (id) => {
    setOpen(false);
    goTo(id);
  };

  const light = !scrolled && !open;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        light ? "border-b border-transparent bg-transparent" : "border-b border-[#E5E0D5]/80 bg-[#F9F8F3]/85 backdrop-blur-xl"
      }`}
    >
      {seasonalActive && (
        <div data-testid="seasonal-banner" className="bg-[#233D25] px-4 py-2 text-center text-sm font-medium text-[#F9F8F3]">
          {SEASONAL.text}
        </div>
      )}
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <button data-testid="header-logo" onClick={() => go("top")} aria-label="Início">
          <Logo light={light} />
        </button>
        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className={`link-underline text-sm font-medium ${light ? "text-[#F9F8F3]/90" : "text-[#1A2218]/80"}`}
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="header-whatsapp-cta"
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            className="group hidden items-center gap-2 rounded-full bg-[#5D995E] px-5 py-2.5 text-sm font-semibold text-[#0F1F10] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8FC690] sm:inline-flex"
          >
            Falar com engenheiro
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`grid h-11 w-11 place-items-center rounded-full border lg:hidden ${light ? "border-white/30 text-white" : "border-[#1A2218]/15 text-[#1A2218]"}`}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#E5E0D5] bg-[#F9F8F3] lg:hidden"
          >
            <div className="flex flex-col px-5 py-6">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => go(n.id)}
                  className="border-b border-[#E5E0D5] py-4 text-left font-serif text-3xl text-[#1A2218]"
                >
                  {n.label}
                </button>
              ))}
              <a
                data-testid="mobile-whatsapp-cta"
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                className="mt-6 rounded-full bg-[#233D25] px-6 py-4 text-center font-semibold text-[#F9F8F3]"
              >
                Falar com engenheiro
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
